import { Box, Button, FormControl, Grid, IconButton } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useDispatch, useSelector } from 'react-redux';
import MUIAutoComplete from '../../../Components/MUIAutoComplete';
import ToolTipComponent from '../../../Components/ToolTipComponent';
import InputField from '../../../Components/Input';
import Images from '../../../Utils/images';
import CustomButton from '../../../Components/CustomButton';
import { RootState, useAppSelector } from '../../../redux/store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { COUNTRY_LIST } from '../../../assets/json/country_dial_info';
import {
  getAllStudiosList,
  getUserDetailsWithPhone,
} from '../../../redux/StudioSlice';
import { notify } from '../../../Utils/toastify';
import SelectPhoneNumber from '../../../Components/SelectPhoneNumber';

const MobileNumbersLength = [5, 6, 7, 8, 9, 10, 11, 12, 13];

const ClientSection = ({
  control,
  errors,
  setValue,
  addButton,
  getValues,
  disabled,
  watch,
  handleClose,
  clearErrors,
}: any) => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { allStudiolist } = useAppSelector((state: RootState) => state.studio);
  const { productionId }: any = useParams();

  const { contacts } = useSelector((state: any) => state.production);

  // Add loading state for phone number validation
  const [loadingStates, setLoadingStates] = useState<{
    [key: number]: boolean;
  }>({});

  const { fields, append, remove, replace, update }: any = useFieldArray({
    control,
    name: 'clients',
  });

  const getClientsValues: any = useWatch({
    name: 'clients',
    control,
  });

  const studioWatch: any = useWatch({
    name: 'studio',
    control,
  });

  useEffect(() => {
    // Find and filter out the item with isNew: true
    const updatedStudios = studioWatch?.filter((item: any) => !item.isNew);

    if (updatedStudios?.length !== studioWatch?.length) {
      // Use setValue to update the field array
      setValue('studio', updatedStudios);
      // Save current form data
      localStorage.setItem(
        'createProductionFormData',
        JSON.stringify(getValues()),
      );
      if (productionId) {
        navigate(`/create-studio?productionId=${productionId}`, {
          state: { from: 'create-production' },
        });
      } else {
        navigate(`/create-studio`, {
          state: { from: 'create-production' },
        });
      }
      // Redirect to create studio

      // handleClose();
    } else {
      // console.log('No item with "isNew" found.')
    }
  }, [studioWatch, setValue]);

  const hasDuplicate = (arr: any, prop1: any, prop2: any) => {
    const map: any = {};

    for (const item of arr) {
      const key = `${item[prop1]}|${item[prop2]}`;

      if (map[key]) {
        return true; // Found a duplicate for the two properties
      } else {
        map[key] = true; // Mark the combination of the two properties as seen
      }
    }

    return false; // No duplicates found
  };

  const hasDuplicates = hasDuplicate(
    getClientsValues,
    'phoneNo',
    'countryCode',
  );

  // Improved debounce implementation
  const debounce = useCallback((func: any, delay: any) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }, []);

  const { state } = useLocation();

  // const wathcStudio = watch('studio')
  const [editMode, setEditMode] = useState(
    state?.isAdmin ? false : state?.editProduction ? false : true,
  );
  // const { countryList } = useSelector((state: any) => state.companyManagement)
  const { productionStudioList } = useAppSelector(
    (state: any) => state.production,
  );

  // Improved phone number validation function
  const validatePhoneNumber = useCallback(
    debounce(async (index: number, phoneNo: string, countryCode: any) => {
      if (!phoneNo || !countryCode?.dial_code) return;

      setLoadingStates(prev => ({ ...prev, [index]: true }));

      try {
        const response = await new Promise((resolve, reject) => {
          dispatch(
            getUserDetailsWithPhone(
              {
                phoneNo: phoneNo,
                countryCode: countryCode?.dial_code,
                countryAbbreviation: countryCode?.code,
              },
              (data: any) => {
                if (
                  data?.message ===
                  "User doesn't exist. Please enter an existing mobile number."
                ) {
                  resolve({ exists: false, data: null });
                } else if (data) {
                  resolve({ exists: true, data: data.data });
                } else {
                  reject(new Error('Failed to fetch user details'));
                }
              },
            ),
          );
        });

        const { exists, data } = response as any;

        if (exists && data) {
          // User exists - populate fields and disable
          update(index, {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phoneNo: phoneNo,
            email: data.email || '',
            ownerId: data._id,
            isDisabled: true,
            country: countryCode?.name,
            countryCode: countryCode,
            countryAbbreviation: countryCode?.code,
          });

          clearErrors(`clients[${index}].firstName`);
          clearErrors(`clients[${index}].lastName`);
          clearErrors(`clients[${index}].email`);

          // notify('User found! Fields have been auto-populated.', 'success');
        } else {
          // User doesn't exist - clear fields and enable editing
          setValue(`clients[${index}].firstName`, '');
          setValue(`clients[${index}].lastName`, '');
          setValue(`clients[${index}].email`, '');
          setValue(`clients[${index}].countryAbbreviation`, countryCode?.code);
          setValue(`clients[${index}].countryCode`, countryCode);
          setValue(`clients[${index}].country`, countryCode?.name);

          // Update the field to enable editing
          const currentField = fields[index];
          if (currentField) {
            currentField.isDisabled = false;
          }
        }
      } catch (error) {
        console.error('Error validating phone number:', error);
        notify('Error validating phone number. Please try again.', 'error');
      } finally {
        setLoadingStates(prev => ({ ...prev, [index]: false }));
      }
    }, 800),
    [dispatch, update, setValue, clearErrors, fields, notify],
  );

  const handleNameChange = (index: number, name: string, e: any) => {
    // Update the name in the current field
    const value = e.target.value;
    const formValues = getValues('clients');
    const headsValues = getValues('productionHead');

    const phoneNo: any = formValues[index].phoneNo;
    const countryCode: any = formValues[index].countryCode;

    const isDuplicate: any = contacts?.some(
      (client: any, idx: number) =>
        client.phoneNo === phoneNo &&
        client.countryCode === countryCode?.dial_code &&
        client.countryAbbreviation === countryCode?.code &&
        client.country === countryCode?.name,
    );

    if (isDuplicate) {
      notify('This phone number already exists in contacts.', 'error');
      setValue(`clients[${index}].phoneNo`, '');
      return;
    }

    if (name === 'phoneNo' || name === 'countryCode') {
      // Check if already exists in production head
      if (
        headsValues?.find(
          (item: any) =>
            item.phoneNo === formValues[index].phoneNo &&
            item.countryCode?.dial_code ===
              formValues[index].countryCode?.dial_code,
        )
      ) {
        toast.error('This owner already added in production head');
        return;
      }

      // Check for duplicates within the same form
      if (
        fields?.find(
          (owner: any, i: number) =>
            index !== i &&
            owner.phoneNo === formValues[index].phoneNo &&
            owner.countryCode?.dial_code ===
              formValues[index].countryCode?.dial_code,
        )
      ) {
        toast.error('This phone number already added!');
        return;
      }

      // Validate phone number length and trigger API call
      if (
        MobileNumbersLength.includes(value?.length) ||
        MobileNumbersLength.includes(formValues[index].phoneNo.length)
      ) {
        validatePhoneNumber(index, phoneNo, countryCode);
      }
    }
  };

  const handleAddEntity = () => {
    append({
      firstName: '',
      lastName: '',
      phoneNo: '',
      email: '',
      countryCode: {
        name: 'United States',
        flag: '🇺🇸',
        code: 'US',
        dial_code: '+1',
      },
      isDisabled: false,
    });
  };

  const handleRemoveEntity = (index: number) => {
    remove(index);
    // Clear loading state for removed field
    setLoadingStates(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
    if (getValues('clients')?.length === 0) {
      handleAddEntity();
    }
  };

  const handleBackSpace = (e: any, index: number, status?: boolean) => {
    if (
      (e.key === 'Backspace' ||
        e.key === 'Delete' ||
        (e.ctrlKey && e.key === 'x')) &&
      status
    ) {
      setValue(`clients[${index}].firstName`, '');
      setValue(`clients[${index}].lastName`, '');
      setValue(`clients[${index}].email`, '');

      // Update the field to enable editing
      const currentField = fields[index];
      if (currentField) {
        currentField.isDisabled = false;
      }

      notify('Fields cleared. You can now edit the information.', 'info');
    }
  };

  useEffect(() => {
    const inputType: any = document.querySelector('#client_input_phone');

    if (inputType) {
      inputType.addEventListener('keypress', (evt: any) => {
        if (evt.which === 8) {
          return;
        }
        if (evt.which < 48 || evt.which > 57) {
          evt.preventDefault();
        }
      });
    }

    inputType?.addEventListener('keydown', function (e: any) {
      if (e.which === 38 || e.which === 40) {
        e.preventDefault();
      }
    });
  }, []);

  const handleKeyDown = (event: any) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      return;
    }
  };

  const companyOptions =
    productionStudioList &&
    productionStudioList?.length > 0 &&
    productionStudioList;

  // Helper function to check if field should be disabled
  const isFieldDisabled = (item: any, fieldName?: string) => {
    if (disabled) return true;
    if (item?._id) return true;
    // Phone number and country code should always be editable
    if (fieldName === 'phoneNo' || fieldName === 'countryCode') {
      return false;
    }
    if (item?.isDisabled) return true;
    return false;
  };

  // Handle clicking on "Create new Studio/Prod Co." from the autocomplete option
  const handleCreateStudioClick = () => {
    // Save current form data
    localStorage.setItem(
      'createProductionFormData',
      JSON.stringify(getValues()),
    );

    // Navigate to create-studio
    if (productionId) {
      navigate(`/create-studio?productionId=${productionId}`, {
        state: { from: 'create-production' },
      });
    } else {
      navigate(`/create-studio`, {
        state: { from: 'create-production' },
      });
    }
  };

  return (
    <>
      <div className="formContainer">
        <Grid container spacing={2} pb={1.5}>
          <Grid item md={6} xs={12}>
            <MUIAutoComplete
              control={control}
              name="studio"
              labelText="Select Studio"
              id={'studio'}
              defaultValue={getValues('studio') || []}
              multiple
              keyName="name"
              type="studio"
              options={
                allStudiolist?.studios?.filter(
                  (item: any, index: any, self: any) => {
                    // Keep items without _id or the first occurrence of an _id
                    return (
                      !item._id ||
                      index ===
                        self.findIndex((other: any) => other._id === item._id)
                    );
                  },
                ) || []
              }
              limit={2}
              setValue={setValue}
              clearIcon
              error={errors['studio']?.message}
              disabled={disabled}
              placeHolderText={
                getValues('studio')?.length > 0 ? '' : 'Select Studio'
              }
              addOptionText={'Create new Studio/Prod Co.'}
              isScroll={true}
              fetchMoreOptions={getAllStudiosList}
              hasMoreOptions={true}
              // disableSearch
              isEvent
              isAddStudio
              onAdd={handleCreateStudioClick}
              // studioListLoading={studioListLoading}
            />
          </Grid>
          {disabled && getValues('studio')?.length > 1 && (
            <Grid item xs={4}>
              <ToolTipComponent
                theme="dark"
                title={getValues('studio')
                  ?.map((item: any) => item.name)
                  ?.join(', ')}
              >
                <Button variant="text" id="">
                  View all
                </Button>
              </ToolTipComponent>
            </Grid>
          )}
        </Grid>

        <>
          {getValues('clients')?.length !== 0 && (
            <Box my={3}>
              <h5>Client Details </h5>
            </Box>
          )}

          {fields?.map((item: any, index: number) => {
            return (
              <Grid container spacing={2} key={item.id}>
                <Grid item md={4} xs={12}>
                  {' '}
                  <InputField
                    id={'client_input_phone'}
                    label={'Mobile Number'}
                    name={`clients[${index}].phoneNo`}
                    placeholder="Enter Mobile number"
                    type="number"
                    control={control}
                    disabled={isFieldDisabled(item, 'phoneNo')}
                    phone
                    maxLength={14}
                    error={
                      !!errors['clients'] && !!errors['clients'][index]?.phoneNo
                    }
                    getOnChangeValues={(e: any) => {
                      handleNameChange(index, 'phoneNo', e);
                    }}
                    helperText={
                      !!errors['clients']
                        ? errors['clients'][index]?.phoneNo?.message
                        : ''
                    }
                    onKeyUp={(e: any) =>
                      handleBackSpace(e, index, item.isDisabled)
                    }
                    onKeyDown={handleKeyDown}
                    startAdornment={
                      <div className="select_in_field">
                        <FormControl fullWidth className="customSelect">
                          <Controller
                            name={`clients[${index}].countryCode`}
                            control={control}
                            defaultValue={'+1US'}
                            render={({ field }) =>
                              field && (
                                <SelectPhoneNumber
                                  control={control}
                                  name={`clients[${index}].countryCode`}
                                  labelText=""
                                  id={'countryCode'}
                                  keyName="dial_code"
                                  options={COUNTRY_LIST}
                                  disabled={isFieldDisabled(
                                    item,
                                    'countryCode',
                                  )}
                                  setValue={setValue}
                                  clearIcon
                                  limit={10}
                                  onChangeMethod={(e: any) =>
                                    handleNameChange(index, 'countryCode', e)
                                  }
                                />
                              )
                            }
                          />
                        </FormControl>
                      </div>
                    }
                    // endAdornment={
                    //   loadingStates[index] ? (
                    //     <CircularProgress
                    //       size={20}
                    //       style={{ marginRight: 8 }}
                    //     />
                    //   ) : null
                    // }
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  {' '}
                  <InputField
                    id={`${index}-attribute`}
                    label={'First Name'}
                    name={`clients[${index}].firstName`}
                    placeholder="Enter first name"
                    type="text"
                    control={control}
                    getOnChangeValues={(e: any) =>
                      handleNameChange(index, 'firstName', e)
                    }
                    error={
                      !!errors['clients'] &&
                      !!errors['clients'][index]?.firstName
                    }
                    helperText={
                      !!errors['clients']
                        ? errors['clients'][index]?.firstName?.message
                        : ''
                    }
                    disabled={isFieldDisabled(item)}
                    requiredField={
                      watch(`clients[${index}].phoneNo`) ? true : false
                    }
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  {' '}
                  <InputField
                    id={`${index}-attribute`}
                    label={'Last Name'}
                    name={`clients[${index}].lastName`}
                    placeholder="Enter last name"
                    type="text"
                    control={control}
                    getOnChangeValues={(e: any) =>
                      handleNameChange(index, 'lastName', e)
                    }
                    error={
                      !!errors['clients'] &&
                      !!errors['clients'][index]?.lastName
                    }
                    helperText={
                      !!errors['clients']
                        ? errors['clients'][index]?.lastName?.message
                        : ''
                    }
                    disabled={isFieldDisabled(item)}
                    requiredField={
                      watch(`clients[${index}].phoneNo`) ? true : false
                    }
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  {' '}
                  <InputField
                    id={`${index}-attribute`}
                    label={'Email Address'}
                    name={`clients[${index}].email`}
                    placeholder="Enter email"
                    type="text"
                    control={control}
                    error={
                      !!errors['clients'] && !!errors['clients'][index]?.email
                    }
                    onChange={e =>
                      handleNameChange(index, 'email', e.target.value)
                    }
                    helperText={
                      !!errors['clients']
                        ? errors['clients'][index]?.email?.message
                        : ''
                    }
                    disabled={isFieldDisabled(item)}
                    requiredField={
                      watch(`clients[${index}].phoneNo`) ? true : false
                    }
                  />
                </Grid>

                <Grid item md={4} xs={12}>
                  <div
                    className="keyButton"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: '105px',
                      gap: '20px',
                    }}
                  >
                    {((fields?.length > 1 && !disabled) || item?._id) && (
                      <IconButton
                        className="btn_delete"
                        onClick={() => handleRemoveEntity(index)}
                      >
                        <img src={Images.TRASH} alt="Delete" />
                      </IconButton>
                    )}

                    {index === fields.length - 1 && !disabled && (
                      <CustomButton
                        onClick={handleAddEntity}
                        size="large"
                        variant="contained"
                        text={'Add'}
                        width="auto"
                        id="submit"
                        disabled={
                          fields.length > 9 ||
                          !getClientsValues[index]?.firstName ||
                          !getClientsValues[index]?.email ||
                          !getClientsValues[index]?.lastName ||
                          !getClientsValues[index]?.phoneNo ||
                          hasDuplicates ||
                          loadingStates[index]
                        }
                        showIcon={true}
                        iconPosition="start"
                        iconType="svg"
                        icon={<AddOutlinedIcon />}
                        display={fields.length > 9}
                      />
                    )}
                  </div>
                </Grid>
              </Grid>
            );
          })}
        </>
      </div>
    </>
  );
};

export default ClientSection;
